import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Button,
    Card,
    Input,
    Tags,
    Pencil,
    Trash2,
    Repeat,
    Plus,
    MapPin,
    Phone,
    Clock3,
    FileText,
    Eye,
    EyeOff,
    Upload,
    Search,
} from "../../ui";
import { ConfirmModal } from "../../modals/Confirm";
import { DeleteModal } from "../../modals/Delete";
import LudDashboardModal from "../../modals/LudDashboardModal";
import BranchFormModal from "../../modals/BranchFormModal";
import useApplicationFlows from "../Applications/useApplicationFlows";
import BranchApplicationsManager from "../Applications/BranchApplicationsManager";
import BranchApplicationAccounts from "../Applications/BranchApplicationAccounts";
import "../Applications/Applications.css";
import BranchUserModal from "../../modals/BranchUserModal";
import BranchStatusModal from "../../modals/BranchStatusModal";
import BranchIntegrationStatusModal from "../../modals/BranchIntegrationStatusModal";
import WorkingHoursModal from "../../modals/WorkingHoursModal";
import { hoursErrorMessage } from "../../../utils/workingHoursErrors";
import SubscriptionLockNotice, { BillingLink } from "../../subscription/SubscriptionLockNotice";
import BranchUsersSection from "./BranchUsersSection";
import "./Branches.css";
import { integrationLabel } from "../../../utils/integrations";
import {
    LICENSE_ACCEPT,
    licenseFileProblem,
    isValidLocationUrl,
} from "../../../utils/branchValidation";
const DAYS = [
    ["السبت", "Saturday"],
    ["الأحد", "Sunday"],
    ["الاثنين", "Monday"],
    ["الثلاثاء", "Tuesday"],
    ["الأربعاء", "Wednesday"],
    ["الخميس", "Thursday"],
    ["الجمعة", "Friday"],
];
const defaultHours = () => DAYS.map(([dayAr, dayEn]) => ({
    dayAr,
    dayEn,
    closed: false,
    from1: "09:00",
    to1: "14:00",
    from2: "17:00",
    to2: "01:00",
}));
const STATUS_LABELS = {
    new: ["جديد", "New"],
    inProgress: ["جاري العمل", "In Progress"],
    active: ["فعال", "Active"],
    inactive: ["غير فعال", "Inactive"],
    temporarilyClosed: ["مغلق مؤقت", "Temporarily Closed"],
    closed: ["مغلق", "Closed"],
};
const INTEGRATION_STATUS_LABELS = {
    active: ["نشط", "Active"],
    inProgress: ["جاري العمل", "In Progress"],
    inactive: ["غير نشط", "Inactive"],
};
const MAX_CARD_APPS = 3;
const appStatusVariant = (link) => {
    if (link?.status === "active") return "success";
    if (link?.status === "inProgress") return "warning";
    return "danger";
};
const statusVariant = (status) => {
    if (status === "active")
        return "success";
    if (status === "closed" || status === "inactive")
        return "danger";
    if (status === "temporarilyClosed" || status === "inProgress")
        return "warning";
    return "neutral";
};
const formatShifts = (day) => {
    if (!day || day.closed || !day.from1) return "";
    return [`${day.from1} – ${day.to1}`, day.from2 ? `${day.from2} – ${day.to2}` : ""].filter(Boolean).join("  ·  ");
};
const todayEnglishName = () => new Date().toLocaleDateString("en-US", { weekday: "long" });
const formatSize = (bytes = 0) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
const formatDate = (value, lang) => {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB", { year: "numeric", month: "short", day: "numeric" });
};
const hasAnyHours = (branch) => (branch.hours || []).some((day) => !day.closed && day.from1);

function InfoRow({ label, children, className = "" }) {
    return (<div className={`lud-branch-info-row ${className}`}>
      <span className="lud-branch-info-label">{label}</span>
      <div className="lud-branch-info-value">{children}</div>
    </div>);
}

// A branch the client may still edit as a draft: it was never made operational (the server applies the same rule).
const isDraftBranch = (branch) => {
    const raw = branch?.raw || {};
    return String(raw.status || "new") === "new" && String(raw.dashboardStatus || "new") === "new" && String(raw.integrationStatus || "inactive") === "inactive";
};

// Is the branch operational today (active in any of its statuses)? Only branches that are NOT operational need coverage to be activated.
const isOperationalBranch = (branch) => {
    const raw = branch?.raw || {};
    return [raw.status, raw.dashboardStatus, raw.integrationStatus].some((value) => String(value) === "active");
};

/*
  Branch management for every client (new / store / storeL) and for LUD staff.
  `prep` (the client owner's preparation API, from the access layer): DRAFT branches are created, edited, completed (working hours, license)
  and removed directly - no subscription is needed to prepare a branch - and branch users (`role: branch`) are created, selected and
  assigned. Branches that are already operational keep the request workflow.
  Activation is the only thing that needs coverage: `coverage` maps a branch id to "covered today" (from the subscription summary, the same
  rules as the entitlement); an uncovered branch that is not operational yet shows its activation action disabled with the reason and a link
  to the billing page. `lock` (no covered branch at all, `{ reason }`) adds the one-line explanation at the top. Preparing is never blocked.
*/
export default function Branches({ lang, text, branches, role, storeId, prep = null, coverage = null, lock = null, onChanged, onGoToCompletion, onAddBranch, onSaveHours, onUpdateBranch, onUpdateBranchStatus, onUpdateBranchIntegrationStatus, onAddBranchUser, onDeleteBranch, onUploadBranchLicense, onRemoveBranchLicense, onGetBranchLicenseUrl, }) {
    const ar = lang === "ar";
    const isAdmin = role === "admin";
    const canPrepare = (branch) => Boolean(prep) && isDraftBranch(branch);
    const canEditBranch = (branch) => isAdmin || canPrepare(branch);
    const canLicense = (branch) => isAdmin || canPrepare(branch);
    // Activation needs coverage of THIS branch (a branch already operational can still be changed or closed).
    const activationLocked = (branch) => !isAdmin && !isOperationalBranch(branch) && (coverage ? coverage[branch.id] === false || (coverage[branch.id] === undefined && Boolean(lock)) : Boolean(lock));
    const lockTitle = ar ? "التفعيل يتطلب اشتراكًا يغطي هذا الفرع" : "Activation needs a subscription that covers this branch";
    const [modal, setModal] = useState(null);
    const [detailsId, setDetailsId] = useState(null);
    const [selectedBranchId, setSelectedBranchId] = useState(null);
    const [hoursForm, setHoursForm] = useState([]);
    const [confirmAction, setConfirmAction] = useState(null);
    const [deleteBranchId, setDeleteBranchId] = useState(null);
    const [deleteError, setDeleteError] = useState("");
    const [query, setQuery] = useState("");
    const [toast, setToast] = useState(null);
    const [licenseBusy, setLicenseBusy] = useState("");
    const [licensePreview, setLicensePreview] = useState(null);
    const [revealedUsers, setRevealedUsers] = useState({});
    const [managerId, setManagerId] = useState(null);
    // The same application workflows as the Applications page (one shared implementation).
    const flows = useApplicationFlows({ lang, storeId, role, onChanged, onGoToCompletion, autoLoad: false });
    const toastTimer = useRef(null);
    const licenseInputRef = useRef(null);

    const selectedBranch = useMemo(() => branches.find((branch) => branch.id === selectedBranchId) || null, [branches, selectedBranchId]);
    const managerBranch = useMemo(() => branches.find((branch) => branch.id === managerId) || null, [branches, managerId]);
    const detailsBranch = useMemo(() => branches.find((branch) => branch.id === detailsId) || null, [branches, detailsId]);
    const visibleBranches = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return branches;
        return branches.filter((branch) => [branch.nameAr, branch.nameEn, branch.cityAr, branch.cityEn, branch.phone]
            .some((value) => String(value || "").toLowerCase().includes(needle)));
    }, [branches, query]);

    useEffect(() => () => clearTimeout(toastTimer.current), []);
    const notify = useCallback((type, message) => {
        clearTimeout(toastTimer.current);
        setToast({ type, message });
        toastTimer.current = setTimeout(() => setToast(null), type === "error" ? 7000 : 4500);
    }, []);

    // Runs an operation from the hook: awaits it, reports the outcome, and
    // tells the caller whether it worked so modals only close on success.
    const perform = useCallback(async (action, doneAr, doneEn) => {
        try {
            const result = await action();
            notify("success", result === "requested"
                ? (ar ? "تم إرسال الطلب للمراجعة" : "Request sent for review")
                : (ar ? doneAr : doneEn));
            return true;
        }
        catch (error) {
            notify("error", hoursErrorMessage(error, ar ? "ar" : "en") || (ar ? "حدث خطأ غير متوقع" : "Something went wrong"));
            return false;
        }
    }, [ar, notify]);

    const openForBranch = (type, branch) => {
        setSelectedBranchId(branch.id);
        setModal(type);
    };
    const editHours = (branch) => {
        setSelectedBranchId(branch.id);
        setHoursForm(branch.hours?.length ? branch.hours.map((day) => ({ ...day })) : defaultHours());
        setModal("hours");
    };
    const openDetails = (branch) => {
        setDetailsId(branch.id);
        setSelectedBranchId(branch.id);
    };

    const saveStatus = (status) => {
        setConfirmAction({
            title: ar ? "تأكيد تغيير حالة الفرع" : "Confirm Branch Status",
            message: ar
                ? `هل تريد تغيير حالة الفرع إلى «${STATUS_LABELS[status]?.[0] || status}»؟`
                : `Change branch status to “${STATUS_LABELS[status]?.[1] || status}”?`,
            onConfirm: async () => {
                setConfirmAction(null);
                const ok = await perform(() => onUpdateBranchStatus(selectedBranch.id, status), "تم تغيير حالة الفرع", "Branch status updated");
                if (ok) setModal(null);
            },
        });
    };
    const saveIntegrationStatus = (status) => {
        setConfirmAction({
            title: ar ? "تأكيد تغيير حالة التكامل" : "Confirm Integration Status",
            message: ar
                ? `هل تريد تغيير حالة التكامل إلى «${INTEGRATION_STATUS_LABELS[status]?.[0] || status}»؟`
                : `Change integration status to “${INTEGRATION_STATUS_LABELS[status]?.[1] || status}”?`,
            onConfirm: async () => {
                setConfirmAction(null);
                const ok = await perform(() => onUpdateBranchIntegrationStatus(selectedBranch.id, status), "تم تغيير حالة التكامل", "Integration status updated");
                if (ok) setModal(null);
            },
        });
    };

    const saveNewBranch = async (form) => {
        const hours = defaultHours().map((day) => ({
            ...day,
            from1: form.from1,
            to1: form.to1,
            from2: form.hasSecondShift ? form.from2 : "",
            to2: form.hasSecondShift ? form.to2 : "",
            note: form.hoursNote,
        }));
        const ok = await perform(() => prep
            ? prep.createBranch({ name: form.nameAr, city: form.cityAr, phone: form.phone, location: form.locationUrl })
            : onAddBranch({
                ...form,
                apps: form.linkedApps,
                requestedApps: form.requestedApps,
                hours,
            }), "تمت إضافة الفرع", "Branch added");
        if (ok) setModal(null);
    };
    const saveBranchEdits = async (form) => {
        // Only the editable branch fields — not the whole display object.
        const changes = {
            nameAr: form.nameAr,
            cityAr: form.cityAr,
            phone: form.phone,
            locationUrl: form.locationUrl,
        };
        const ok = await perform(() => canPrepare(selectedBranch)
            ? prep.updateBranch(selectedBranch.id, { name: changes.nameAr, city: changes.cityAr, phone: changes.phone, location: changes.locationUrl })
            : onUpdateBranch(selectedBranch.id, changes), "تم حفظ بيانات الفرع", "Branch saved");
        if (ok) setModal(null);
    };
    const confirmDelete = async (id) => {
        setDeleteError("");
        try {
            const target = branches.find((entry) => entry.id === id);
            if (target && canPrepare(target)) await prep.removeBranch(id);
            else await onDeleteBranch(id);
        }
        catch (error) {
            setDeleteError(error?.message || (ar ? "تعذر حذف الفرع" : "Could not delete the branch"));
            return;
        }
        setDeleteBranchId(null);
        if (detailsId === id) setDetailsId(null);
        notify("success", isAdmin || prep
            ? (ar ? "تم حذف الفرع" : "Branch deleted")
            : (ar ? "تم إرسال الطلب للمراجعة" : "Request sent for review"));
    };

    // ---- License ----
    const pickLicenseFile = async (event, branch) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        const problem = licenseFileProblem(file);
        if (problem) {
            notify("error", problem === "size"
                ? (ar ? "حجم الملف يتجاوز 10 ميغابايت" : "The file is larger than 10 MB")
                : (ar ? "النوع غير مدعوم. ارفع PDF أو صورة (JPG / PNG / WEBP)" : "Unsupported file. Upload a PDF or an image (JPG / PNG / WEBP)"));
            return;
        }
        setLicenseBusy("upload");
        await perform(() => (canPrepare(branch) ? prep.uploadLicense(branch.id, file) : onUploadBranchLicense(branch.id, file)), branch.license ? "تم استبدال الرخصة" : "تم رفع الرخصة", branch.license ? "License replaced" : "License uploaded");
        setLicenseBusy("");
    };
    const previewLicense = async (branch) => {
        setLicenseBusy("preview");
        try {
            const url = await onGetBranchLicenseUrl(branch.id);
            setLicensePreview({ url, name: branch.license?.name, mimeType: branch.license?.mimeType, branchName: ar ? branch.nameAr : branch.nameEn });
        }
        catch (error) {
            notify("error", error?.message || (ar ? "تعذر فتح الرخصة" : "Could not open the license"));
        }
        setLicenseBusy("");
    };
    const requestLicenseRemoval = (branch) => {
        setConfirmAction({
            title: ar ? "حذف رخصة الفرع" : "Delete Branch License",
            message: ar
                ? "سيتم حذف ملف الرخصة نهائياً من هذا الفرع. هل تريد المتابعة؟"
                : "The license file will be permanently removed from this branch. Continue?",
            onConfirm: async () => {
                setConfirmAction(null);
                setLicenseBusy("remove");
                await perform(() => (canPrepare(branch) ? prep.removeLicense(branch.id) : onRemoveBranchLicense(branch.id)), "تم حذف الرخصة", "License deleted");
                setLicenseBusy("");
            },
        });
    };

    const renderCard = (branch) => {
        const name = ar ? branch.nameAr : branch.nameEn;
        const city = ar ? branch.cityAr : branch.cityEn;
        const statusLabel = STATUS_LABELS[branch.status] || STATUS_LABELS.active;
        const today = (branch.hours || []).find((day) => day.dayEn === todayEnglishName());
        const todayText = formatShifts(today);
        const appLinks = branch.appLinks || [];
        return (<article className="lud-branch-card" key={branch.id}>
        <header className="lud-branch-card-head">
          <div className="lud-branch-card-title">
            <h3 title={name}>{name || (ar ? "فرع بدون اسم" : "Unnamed branch")}</h3>
            {city && <span>{city}</span>}
          </div>
          <button type="button" className="lud-branch-status-tag" disabled={activationLocked(branch)} onClick={() => openForBranch("status", branch)} title={activationLocked(branch) ? lockTitle : (ar ? "تغيير حالة الفرع" : "Change Branch Status")} aria-label={ar ? "تغيير حالة الفرع" : "Change Branch Status"}>
            <Tags variant={statusVariant(branch.status)} appearance="soft" size="sm">
              {ar ? statusLabel[0] : statusLabel[1]}
            </Tags>
          </button>
        </header>

        <ul className="lud-branch-card-facts">
          <li>
            <Phone size={14} aria-hidden="true"/>
            {branch.phone ? <a href={`tel:${branch.phone}`} dir="ltr">{branch.phone}</a> : <em>{ar ? "لا يوجد رقم" : "No phone"}</em>}
          </li>
          <li>
            <MapPin size={14} aria-hidden="true"/>
            {branch.locationUrl ? <a href={branch.locationUrl} target="_blank" rel="noreferrer noopener" title={branch.locationUrl}>{ar ? "فتح الموقع" : "Open location"}</a> : <em>{ar ? "لا يوجد رابط موقع" : "No location link"}</em>}
          </li>
          <li>
            <Clock3 size={14} aria-hidden="true"/>
            {!hasAnyHours(branch)
                ? <em>{ar ? "لم تحدد أوقات العمل" : "Hours not set"}</em>
                : todayText
                    ? <span><b>{ar ? "اليوم:" : "Today:"}</b> <bdi dir="ltr">{todayText}</bdi></span>
                    : <span>{ar ? "مغلق اليوم" : "Closed today"}</span>}
          </li>
          <li>
            <FileText size={14} aria-hidden="true"/>
            {branch.license
                ? <Tags variant="success" appearance="soft" size="sm">{ar ? "الرخصة مرفوعة" : "License uploaded"}</Tags>
                : <Tags variant="warning" appearance="soft" size="sm">{ar ? "لا توجد رخصة" : "No license"}</Tags>}
          </li>
        </ul>

        {appLinks.length > 0 && (<div className="lud-branch-apps">
            {appLinks.slice(0, MAX_CARD_APPS).map((link) => (<Tags key={`${link.app}-${link.id}`} variant={appStatusVariant(link)} appearance="soft" size="sm">
                {integrationLabel(link.app, lang)}
              </Tags>))}
            {appLinks.length > MAX_CARD_APPS && <Tags variant="neutral" appearance="soft" size="sm">+{appLinks.length - MAX_CARD_APPS}</Tags>}
          </div>)}

        {activationLocked(branch) && (<p className="lud-branch-activation-note" data-testid="activation-locked">
            {ar ? "التفعيل يتطلب اشتراكًا يغطي هذا الفرع." : "Activation needs a subscription that covers this branch."} <BillingLink lang={lang}/>
          </p>)}

        <footer className="lud-branch-card-actions">
          <Button size="sm" variant="primary" appearance="soft" onClick={() => openDetails(branch)}>
            {ar ? "التفاصيل" : "Details"}
          </Button>
          <Button size="sm" variant="neutral" appearance="soft" icon={<Repeat size={14}/>} onClick={() => setManagerId(branch.id)} aria-label={ar ? "إدارة التطبيقات" : "Manage Applications"} title={ar ? "إدارة التطبيقات" : "Manage Applications"}>
            {ar ? "التطبيقات" : "Applications"}
          </Button>
          <div className="lud-branch-card-icons">
            {canEditBranch(branch) && (<Button className="lud-branch-action-button" size="sm" rounded icon={<Pencil size={16}/>} onClick={() => openForBranch("edit", branch)} title={ar ? "تعديل بيانات الفرع" : "Edit Branch"} aria-label={ar ? "تعديل بيانات الفرع" : "Edit Branch"}/>)}
            {canEditBranch(branch) && (<Button className="lud-branch-action-button" size="sm" rounded variant="danger" appearance="soft" icon={<Trash2 size={16}/>} onClick={() => { setDeleteError(""); setDeleteBranchId(branch.id); }} title={ar ? "حذف الفرع" : "Delete Branch"} aria-label={ar ? "حذف الفرع" : "Delete Branch"}/>)}
          </div>
        </footer>
      </article>);
    };

    const renderDetails = (branch) => {
        const integrationStatusLabel = INTEGRATION_STATUS_LABELS[branch.integrationStatus] || INTEGRATION_STATUS_LABELS.inactive;
        const users = branch.users || [];
        const license = branch.license;
        const hours = branch.hours?.length ? branch.hours : defaultHours();
        const safeLocation = branch.locationUrl && isValidLocationUrl(branch.locationUrl);
        return (<div className="lud-branch-details">
        <div className="lud-branch-toolbar">
          <Button size="sm" appearance="soft" icon={<Repeat size={16}/>} onClick={() => setManagerId(branch.id)}>{ar ? "إدارة التطبيقات" : "Manage Applications"}</Button>
          {canEditBranch(branch) && <Button size="sm" appearance="soft" icon={<Pencil size={16}/>} onClick={() => openForBranch("edit", branch)}>{ar ? "تعديل البيانات" : "Edit Data"}</Button>}
          {canEditBranch(branch) && <Button size="sm" variant="danger" appearance="soft" icon={<Trash2 size={16}/>} onClick={() => { setDeleteError(""); setDeleteBranchId(branch.id); }}>{ar ? "حذف الفرع" : "Delete Branch"}</Button>}
        </div>

        <section className="lud-branch-section">
          <h4>{ar ? "معلومات الفرع" : "Branch Information"}</h4>
          <div className="lud-branch-info-grid">
            <InfoRow label={ar ? "اسم الفرع" : "Branch name"}>{(ar ? branch.nameAr : branch.nameEn) || "—"}</InfoRow>
            <InfoRow label={ar ? "المدينة" : "City"}>{(ar ? branch.cityAr : branch.cityEn) || "—"}</InfoRow>
            <InfoRow label={ar ? "رقم التواصل" : "Phone"}>{branch.phone ? <a href={`tel:${branch.phone}`} dir="ltr">{branch.phone}</a> : "—"}</InfoRow>
            <InfoRow label={ar ? "رابط الموقع" : "Location"}>
              {safeLocation
                  ? <a href={branch.locationUrl} target="_blank" rel="noreferrer noopener" className="lud-branch-url" dir="ltr" title={branch.locationUrl}>{branch.locationUrl}</a>
                  : (branch.locationUrl || "—")}
            </InfoRow>
            <InfoRow label={ar ? "حالة الفرع" : "Branch status"}>
              <button type="button" className="lud-branch-status-tag" disabled={activationLocked(branch)} onClick={() => openForBranch("status", branch)} title={activationLocked(branch) ? lockTitle : (ar ? "تغيير حالة الفرع" : "Change Branch Status")}>
                <Tags variant={statusVariant(branch.status)} appearance="soft" size="sm">{ar ? (STATUS_LABELS[branch.status] || STATUS_LABELS.active)[0] : (STATUS_LABELS[branch.status] || STATUS_LABELS.active)[1]}</Tags>
              </button>
            </InfoRow>
            <InfoRow label={ar ? "التكامل" : "Integration"}>
              <button type="button" className="lud-branch-integration-status" disabled={activationLocked(branch)} onClick={() => openForBranch("integrationStatus", branch)} title={activationLocked(branch) ? lockTitle : (ar ? "تغيير حالة التكامل" : "Change Integration Status")}>
                <Tags variant={branch.integrationStatus === "inactive" ? "danger" : statusVariant(branch.integrationStatus)} appearance="soft" size="sm">
                  {ar ? integrationStatusLabel[0] : integrationStatusLabel[1]}
                </Tags>
              </button>
            </InfoRow>
          </div>
          {safeLocation && (<div className="lud-branch-section-actions">
              <a className="lud-branch-open-link" href={branch.locationUrl} target="_blank" rel="noreferrer noopener">{ar ? "فتح الموقع على الخريطة ↗" : "Open on map ↗"}</a>
            </div>)}
        </section>

        <section className="lud-branch-section">
          <div className="lud-branch-content-head">
            <h4>{ar ? "رخصة الفرع" : "Branch License"}</h4>
            {canLicense(branch) && (<>
                <input ref={licenseInputRef} type="file" accept={LICENSE_ACCEPT} hidden onChange={(event) => pickLicenseFile(event, branch)}/>
                <Button size="sm" appearance="soft" icon={<Upload size={16}/>} loading={licenseBusy === "upload"} disabled={Boolean(licenseBusy)} onClick={() => licenseInputRef.current?.click()}>
                  {license ? (ar ? "استبدال الرخصة" : "Replace") : (ar ? "رفع رخصة" : "Upload license")}
                </Button>
              </>)}
          </div>
          {license ? (<div className="lud-branch-license">
              <FileText size={26} aria-hidden="true"/>
              <div className="lud-branch-license-meta">
                <strong title={license.name}>{license.name || (ar ? "ملف الرخصة" : "License file")}</strong>
                <span>{[license.mimeType === "application/pdf" ? "PDF" : (ar ? "صورة" : "Image"), license.size ? formatSize(license.size) : "", formatDate(license.uploadedAt, lang)].filter(Boolean).join(" · ")}</span>
              </div>
              <div className="lud-branch-license-actions">
                <Button size="sm" variant="primary" appearance="soft" icon={<Eye size={16}/>} loading={licenseBusy === "preview"} disabled={Boolean(licenseBusy)} onClick={() => previewLicense(branch)}>{ar ? "معاينة" : "Preview"}</Button>
                {canLicense(branch) && <Button size="sm" variant="danger" appearance="soft" icon={<Trash2 size={16}/>} loading={licenseBusy === "remove"} disabled={Boolean(licenseBusy)} onClick={() => requestLicenseRemoval(branch)}>{ar ? "حذف" : "Delete"}</Button>}
              </div>
            </div>) : (<p className="lud-branch-empty">{ar ? "لم يتم رفع رخصة لهذا الفرع. الملفات المدعومة: PDF أو صورة، حتى 10 ميغابايت." : "No license uploaded for this branch. Accepted: PDF or image, up to 10 MB."}</p>)}
        </section>

        {prep && (<section className="lud-branch-section" data-testid="branch-preparation">
          <h4>{ar ? "جاهزية الفرع" : "Branch preparation"}</h4>
          <div className="lud-branch-prep-tags">
            {[
              [ar ? "الاسم" : "Name", Boolean(branch.nameAr)],
              [ar ? "المدينة" : "City", Boolean(branch.cityAr)],
              [ar ? "الجوال" : "Phone", Boolean(branch.phone)],
              [ar ? "الموقع" : "Location", Boolean(safeLocation)],
              [ar ? "أوقات العمل" : "Working hours", hasAnyHours(branch)],
              [ar ? "الرخصة" : "License", Boolean(license)],
            ].map(([label, done]) => (<Tags key={label} variant={done ? "success" : "warning"} appearance="soft" size="sm">{label}: {done ? (ar ? "مكتمل" : "Done") : (ar ? "ناقص" : "Missing")}</Tags>))}
          </div>
        </section>)}

        <section className="lud-branch-section">
          <div className="lud-branch-content-head">
            <h4>{ar ? "أوقات العمل" : "Working Hours"}</h4>
            <Button size="sm" variant="primary" appearance="soft" icon={<Clock3 size={16}/>} onClick={() => editHours(branch)}>{ar ? "تعديل الأوقات" : "Edit Hours"}</Button>
          </div>
          <div className="lud-hours-table-wrap">
            <table className="lud-dashboard-table">
              <thead>
                <tr>
                  <th>{ar ? "اليوم" : "Day"}</th>
                  <th>{ar ? "الفترات" : "Shifts"}</th>
                  <th>{text.status}</th>
                </tr>
              </thead>
              <tbody>
                {hours.map((day) => (<tr key={day.dayEn}>
                    <td>{ar ? day.dayAr : day.dayEn}</td>
                    <td><bdi dir="ltr">{formatShifts(day) || "—"}</bdi></td>
                    <td>{day.closed || !day.from1 ? text.closed : text.active}</td>
                  </tr>))}
              </tbody>
            </table>
          </div>
        </section>

        {prep && storeId && (<section className="lud-branch-section">
          <h4>{ar ? "مستخدم الفرع" : "Branch User"}</h4>
          <BranchUsersSection lang={lang} storeId={storeId} branch={{ id: branch.id, name: ar ? branch.nameAr : branch.nameEn }} onChanged={onChanged}/>
        </section>)}

        <section className="lud-branch-section">
          <div className="lud-branch-content-head">
            <h4>{ar ? "حسابات المستخدم في الفرع" : "Branch User Accounts"}</h4>
            {isAdmin && <Button size="sm" appearance="soft" icon={<Plus size={16}/>} onClick={() => openForBranch("user", branch)}>{ar ? "إضافة يوزر" : "Add User"}</Button>}
          </div>
          <div className="lud-hours-table-wrap">
            <table className="lud-dashboard-table">
              <thead>
                <tr>
                  <th>{ar ? "التطبيق" : "Application"}</th>
                  <th>{ar ? "الإيميل" : "Email"}</th>
                  <th>{ar ? "الرقم السري" : "Password"}</th>
                  <th>{text.status}</th>
                </tr>
              </thead>
              <tbody>
                {users.length ? users.map((user) => {
                    const key = `${branch.id}-${user.id}`;
                    const shown = revealedUsers[key];
                    return (<tr key={user.id || `${user.app}-${user.email}`}>
                      <td>{integrationLabel(user.app, lang)}</td>
                      <td className="lud-branch-wrap-cell">{user.email}</td>
                      <td>
                        <span className="lud-branch-password">
                          <bdi dir="ltr">{user.password ? (shown ? user.password : "••••••••") : "—"}</bdi>
                          {user.password && (<button type="button" className="lud-branch-eye" onClick={() => setRevealedUsers((current) => ({ ...current, [key]: !current[key] }))} aria-label={shown ? (ar ? "إخفاء" : "Hide") : (ar ? "إظهار" : "Show")}>
                              {shown ? <EyeOff size={14}/> : <Eye size={14}/>}
                            </button>)}
                        </span>
                      </td>
                      <td>{user.status === "active" ? text.active : text.closed}</td>
                    </tr>);
                }) : <tr><td colSpan="4">{ar ? "لا توجد حسابات مستخدمين" : "No user accounts"}</td></tr>}
              </tbody>
            </table>
          </div>
        </section>

        <section className="lud-branch-section">
          <h4>{ar ? "حسابات التطبيقات" : "Application Accounts"}</h4>
          {storeId ? <BranchApplicationAccounts branch={branch} storeId={storeId} flows={flows}/> : null}
        </section>
      </div>);
    };

    const previewIsImage = licensePreview?.mimeType?.startsWith("image/");

    return (<section className="lud-dashboard-section">
      <Card className="lud-branches-card">
        <div className="lud-dashboard-section-title">
          <h2>{text.branches} <small>{branches.length}</small></h2>
          <Button size="sm" icon={<Plus size={16}/>} onClick={() => { setSelectedBranchId(null); setModal("addBranch"); }}>
            {prep ? (ar ? "إضافة فرع" : "Add branch") : text.addBranch}
          </Button>
        </div>
        {lock && <SubscriptionLockNotice lang={lang} reason={lock.reason} title={ar ? "تفعيل الفروع" : "Branch activation"}/>}

        {branches.length > 4 && (<div className="lud-branches-search">
            <Input iconStart={<Search size={16}/>} value={query} onChange={(event) => setQuery(event.target.value)} placeholder={ar ? "ابحث بالاسم أو المدينة أو الجوال" : "Search by name, city or phone"} aria-label={ar ? "بحث في الفروع" : "Search branches"}/>
          </div>)}

        {visibleBranches.length ? (<div className="lud-branches-list">
            {visibleBranches.map(renderCard)}
          </div>) : (<p className="lud-branch-empty lud-branches-empty">
            {branches.length ? (ar ? "لا توجد فروع مطابقة للبحث" : "No branches match your search") : (ar ? "لا توجد فروع بعد" : "No branches yet")}
          </p>)}

        {role === "customer" && !prep && <p className="lud-customer-note">{text.requestedByCustomer}</p>}
      </Card>

      <LudDashboardModal open={Boolean(detailsBranch)} size="lg" title={detailsBranch ? `${ar ? "تفاصيل الفرع" : "Branch Details"} — ${ar ? detailsBranch.nameAr : detailsBranch.nameEn}` : ""} onClose={() => setDetailsId(null)} footer={<Button variant="primary" appearance="soft" onClick={() => setDetailsId(null)}>{ar ? "إغلاق" : "Close"}</Button>}>
        {detailsBranch && renderDetails(detailsBranch)}
      </LudDashboardModal>

      <BranchFormModal open={modal === "addBranch"} lang={lang} draft={Boolean(prep)} onClose={() => setModal(null)} onSave={saveNewBranch}/>
      <BranchFormModal open={modal === "edit"} lang={lang} draft={canPrepare(selectedBranch)} branch={selectedBranch} onClose={() => setModal(null)} onSave={saveBranchEdits}
        extra={prep && selectedBranch && storeId ? (<section className="lud-branch-section"><h4>{ar ? "مستخدم الفرع" : "Branch User"}</h4><BranchUsersSection lang={lang} storeId={storeId} branch={{ id: selectedBranch.id, name: ar ? selectedBranch.nameAr : selectedBranch.nameEn }} onChanged={onChanged}/></section>) : null}/>
      <BranchApplicationsManager open={Boolean(managerBranch) && Boolean(storeId)} branch={managerBranch ? { id: managerBranch.id, name: ar ? managerBranch.nameAr : managerBranch.nameEn } : null} storeId={storeId} flows={flows} onClose={() => setManagerId(null)}/>
      {flows.element}
      <BranchUserModal open={modal === "user"} lang={lang} onClose={() => setModal(null)} onSave={async (user) => {
            const ok = await perform(() => onAddBranchUser(selectedBranch.id, user), "تمت إضافة المستخدم", "User added");
            if (ok) setModal(null);
        }}/>
      <BranchStatusModal open={modal === "status"} lang={lang} branch={selectedBranch} onClose={() => setModal(null)} onSave={saveStatus}/>
      <BranchIntegrationStatusModal open={modal === "integrationStatus"} lang={lang} branch={selectedBranch} onClose={() => setModal(null)} onSave={saveIntegrationStatus}/>

      <WorkingHoursModal open={modal === "hours"} lang={lang} text={text} branch={selectedBranch} hours={hoursForm} onClose={() => setModal(null)} onSubmit={async (nextHours) => {
            const ok = await perform(() => (canPrepare(selectedBranch) ? prep.setHours(selectedBranchId, nextHours) : onSaveHours(selectedBranchId, nextHours)), "تم حفظ أوقات العمل", "Working hours saved");
            if (ok) setModal(null);
        }}/>

      <LudDashboardModal open={Boolean(licensePreview)} size="lg" title={licensePreview ? `${ar ? "رخصة الفرع" : "Branch License"} — ${licensePreview.branchName || ""}` : ""} onClose={() => setLicensePreview(null)} footer={<>
          {licensePreview?.url && <a className="lud-branch-open-link" href={licensePreview.url} target="_blank" rel="noreferrer noopener">{ar ? "فتح في تبويب جديد ↗" : "Open in new tab ↗"}</a>}
          <Button variant="primary" appearance="soft" onClick={() => setLicensePreview(null)}>{ar ? "إغلاق" : "Close"}</Button>
        </>}>
        {licensePreview && (<div className="lud-branch-preview">
            {previewIsImage
                ? <img src={licensePreview.url} alt={licensePreview.name || (ar ? "رخصة الفرع" : "Branch license")}/>
                : <iframe src={licensePreview.url} title={licensePreview.name || (ar ? "رخصة الفرع" : "Branch license")}/>}
          </div>)}
      </LudDashboardModal>

      {confirmAction && <ConfirmModal title={confirmAction.title} message={confirmAction.message} onConfirm={confirmAction.onConfirm} onCancel={() => { setConfirmAction(null); }}/>}
      {deleteBranchId && <DeleteModal id={deleteBranchId} error={deleteError} description={ar
            ? "سيتم حذف الفرع مع أوقات عمله وتطبيقاته وحسابات مستخدميه ورخصته. لن يتم حذف أي بيانات أخرى للمتجر."
            : "This removes the branch with its working hours, applications, user accounts and license. No other store data is deleted."} handleDelete={confirmDelete} cancel={() => { setDeleteBranchId(null); setDeleteError(""); }}/>}

      {toast && (<div className={`lud-branch-toast ${toast.type}`} role={toast.type === "error" ? "alert" : "status"} dir={ar ? "rtl" : "ltr"}>
          {toast.message}
        </div>)}
    </section>);
}
